import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Pagination, needsPager, useClientPage, usePagination } from "@strat/ui/pagination";

import { api } from "../../api";
import type { PublicUser, Role } from "../../api";
import { LAYOUT_NAME } from "../../nav";
import { Avatar } from "../../shell/Avatar";
import { Guard } from "../../shell/Guard";
import { Alert, Card, PageHead, Stat } from "../../shell/ui";
import { ActivityTab, SimTab } from "./Ops";
import { BotsTab } from "./Bots";
import { MarketDataTab } from "./MarketData";
import { AlertList, Meter, bytes, duration, since, useOpsAlerts } from "./opsShared";
import { FeedbackTab } from "./Feedback";
import { LimitsTab, MemberLimits } from "./Limits";
import { VerificationTab } from "./Verification";
import { LeagueTab } from "./League";
import type { LimitKey, LimitValues } from "./Limits";

interface Overview {
  users: Array<{ role: Role; kind: "real" | "demo" | "simulated"; n: number }>;
  /** Real members apart from simulated accounts. */
  signupsPerDay: Array<{ day: string; real: number; other: number }>;
  activeUsers: { day: number; week: number; realDay: number; realWeek: number };
  activeSessions: number;
  system: {
    version: string;
    uptimeSeconds: number;
    dbBytes: number | null;
    redisBytes: number | null;
    rssBytes: number;
  };
}

interface AdminUser extends PublicUser {
  isPublic: boolean;
  lastSeenAt: string | null;
  limitOverrides: LimitValues;
  limits: Record<LimitKey, number> | null;
  usage: Record<LimitKey, number>;
}

const TABS = [
  "Overview",
  "Bots",
  "Market data",
  "Activity",
  "Simulation",
  "Feedback",
  "Verification",
  "League",
  "Members",
  "Limits",
  "Audit log",
] as const;
type Tab = (typeof TABS)[number];

function OverviewTab() {
  const alerts = useOpsAlerts();
  const q = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => api<Overview>("/admin/overview"),
    refetchInterval: 15_000,
  });
  if (!q.data) return q.error ? <Alert tone="error">{q.error.message}</Alert> : null;
  const o = q.data;
  const count = (kind: string) => o.users.filter((u) => u.kind === kind).reduce((s, u) => s + u.n, 0);
  const maxDay = Math.max(1, ...o.signupsPerDay.map((d) => d.real));
  return (
    <div className="stack">
      <Card title="Needs attention">
        <AlertList items={alerts.data?.items ?? []} loading={alerts.isPending} showArea />
      </Card>
      <Card title="Members">
        <div className="stat-grid stat-grid--pairs">
          <Stat label="Real members" value={count("real")} hint="Signed up themselves" />
          <Stat label="Demo accounts" value={count("demo")} hint="One-click, shared" />
          <Stat label="Simulated residents" value={count("simulated")} hint="Driven by hub-sim" />
          <Stat
            label="Real members active today"
            value={o.activeUsers.realDay}
            hint={`${o.activeUsers.day} with simulated accounts`}
          />
          <Stat
            label="Real members active this week"
            value={o.activeUsers.realWeek}
            hint={`${o.activeUsers.week} with simulated accounts`}
          />
          <Stat label="Open sessions" value={o.activeSessions} />
        </div>
      </Card>
      <Card title="Sign-ups by real members, last 14 days">
        {o.signupsPerDay.length === 0 ? (
          <p className="muted">No sign-ups in the last two weeks.</p>
        ) : (
          <div className="bars">
            {o.signupsPerDay.map((d) => (
              <div key={d.day} className="bars__row">
                <span className="bars__label mono">{d.day}</span>
                <span className="bars__track">
                  <span className="bars__fill" style={{ width: `${(d.real / maxDay) * 100}%` }} />
                </span>
                <span className="bars__value mono">
                  {d.real}
                  {d.other > 0 && (
                    <span className="muted small" title="Simulated accounts">
                      {" "}
                      +{d.other}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card title="System">
        <div className="stat-grid stat-grid--pairs">
          <Stat label="Version" value={o.system.version} />
          <Stat label="API uptime" value={duration(o.system.uptimeSeconds)} />
          <Stat label="API memory" value={bytes(o.system.rssBytes)} />
          <Stat label="Database size" value={bytes(o.system.dbBytes)} />
          <Stat label="Redis memory" value={bytes(o.system.redisBytes)} />
        </div>
      </Card>
      <ServicesCard />
    </div>
  );
}

interface ServiceBeat {
  service: string;
  instance: string;
  at: number;
  startedAt: number;
  version: string | null;
  rssBytes: number;
  memBytes: number | null;
  memLimitBytes: number | null;
  cpuPct: number | null;
  cpuLimit: number | null;
  load1: number;
  hostCpus: number;
}

interface HealthResponse {
  services: ServiceBeat[];
  expected: string[];
  postgres: { bytes: number | null; connections: number | null; maxConnections: number | null };
  redis: {
    usedBytes: number | null;
    maxBytes: number | null;
    clients: number | null;
    uptimeSeconds: number | null;
  };
  queues: Record<"compute" | "data" | "notify", Record<string, number>>;
  checkedAt: number;
}

const SERVICE_ROLE: Record<string, string> = {
  "hub-api": "Web and API",
  "hub-bots": "Paper bots and feeds",
  "hub-worker": "Backtests and sweeps",
  "hub-scheduler": "Market data sync, league",
  "hub-notify": "Push notifications, alerts",
};

/** Every service's heartbeat: is it there, and what it uses against its container's limits. */
function ServicesCard() {
  const q = useQuery({
    queryKey: ["admin", "health"],
    queryFn: () => api<HealthResponse>("/admin/health"),
    refetchInterval: 20_000,
  });
  if (!q.data) return q.error ? <Alert tone="error">{q.error.message}</Alert> : null;
  const h = q.data;
  const missing = h.expected.filter((s) => !h.services.some((b) => b.service === s));
  const pctOf = (n: number) => `${Math.round(n)}%`;
  return (
    <>
      <Card title="Services" flush>
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Services and the resources they use</caption>
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
                <th className="num">Up</th>
                <th>Memory (container)</th>
                <th>CPU (container)</th>
                <th className="num">Last beat</th>
              </tr>
            </thead>
            <tbody>
              {h.services.map((b) => (
                <tr key={`${b.service}-${b.instance}`}>
                  <td>
                    <strong>{b.service}</strong>{" "}
                    <span className="muted small">
                      {SERVICE_ROLE[b.service] ?? ""}
                      {h.services.filter((x) => x.service === b.service).length > 1
                        ? ` · ${b.instance.slice(0, 6)}`
                        : ""}
                    </span>
                  </td>
                  <td>
                    <span className="bot-state">
                      <span className="health-dot" aria-hidden="true" /> running
                    </span>
                  </td>
                  <td className="num mono small">{since(b.startedAt)}</td>
                  <td>
                    <Meter
                      value={b.memBytes ?? b.rssBytes}
                      limit={b.memLimitBytes}
                      format={bytes}
                      label={`${b.service} memory`}
                    />
                  </td>
                  <td>
                    <Meter
                      value={b.cpuPct}
                      limit={b.cpuLimit ? b.cpuLimit * 100 : null}
                      format={pctOf}
                      label={`${b.service} CPU`}
                    />
                  </td>
                  <td className="num small">{Math.max(0, Math.round((h.checkedAt - b.at) / 1000))}s ago</td>
                </tr>
              ))}
              {missing.map((s) => (
                <tr key={s}>
                  <td>
                    <strong>{s}</strong> <span className="muted small">{SERVICE_ROLE[s] ?? ""}</span>
                  </td>
                  <td colSpan={5}>
                    <span className="bot-state down">
                      <span className="health-dot health-dot--fail" aria-hidden="true" /> no heartbeat
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="card__body muted small">
          Host load {h.services[0]?.load1.toFixed(2) ?? "—"} on {h.services[0]?.hostCpus ?? "?"} cores. Memory
          and CPU are what Docker counts against each container&rsquo;s limit.
        </p>
      </Card>
      <Card title="Database, cache and queues">
        <div className="ops-resources">
          <div>
            <div className="field__label">Postgres connections</div>
            <Meter
              value={h.postgres.connections}
              limit={h.postgres.maxConnections}
              format={(n) => String(n)}
              label="Postgres connections"
            />
          </div>
          <div>
            <div className="field__label">Postgres size</div>
            <span className="mono">{bytes(h.postgres.bytes)}</span>
          </div>
          <div>
            <div className="field__label">Redis memory</div>
            <Meter value={h.redis.usedBytes} limit={h.redis.maxBytes} format={bytes} label="Redis memory" />
          </div>
          <div>
            <div className="field__label">Redis clients</div>
            <span className="mono">{h.redis.clients ?? "—"}</span>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Job queues</caption>
            <thead>
              <tr>
                <th>Queue</th>
                <th className="num">Waiting</th>
                <th className="num">Active</th>
                <th className="num">Delayed</th>
                <th className="num">Failed</th>
                <th className="num">Done (kept)</th>
              </tr>
            </thead>
            <tbody>
              {(["compute", "data", "notify"] as const).map((k) => (
                <tr key={k}>
                  <td>
                    {
                      {
                        compute: "Compute (backtests)",
                        data: "Data (sync, league)",
                        notify: "Notifications",
                      }[k]
                    }
                  </td>
                  <td className="num mono">{h.queues[k].waiting ?? 0}</td>
                  <td className="num mono">{h.queues[k].active ?? 0}</td>
                  <td className="num mono">{h.queues[k].delayed ?? 0}</td>
                  <td className={`num mono${h.queues[k].failed ? " down" : ""}`}>
                    {h.queues[k].failed ?? 0}
                  </td>
                  <td className="num mono">{h.queues[k].completed ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function kindOf(u: AdminUser): string {
  if (u.isDemo) return "demo";
  if (u.isSimulated) return "simulated";
  return "member";
}

function MembersTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const { limit, offset, setLimit, setOffset } = usePagination();
  const list = useQuery({
    queryKey: ["admin", "users", q, limit, offset],
    queryFn: () =>
      api<{ total: number; items: AdminUser[] }>(
        `/admin/users?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}`,
      ),
    placeholderData: (prev) => prev,
  });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) =>
      api(`/admin/users/${id}`, { method: "PATCH", body: { role } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  const vouch = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) =>
      api(`/hub/admin/users/${id}/publisher-badge`, {
        method: "PUT",
        body: { badge: on ? "verified" : null },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin"] }),
  });
  const error = setRole.error ?? remove.error ?? vouch.error;

  return (
    <Card
      title={`Members${list.data ? ` · ${list.data.total}` : ""}`}
      actions={
        <input
          className="filter"
          placeholder="Search username or name"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
        />
      }
      flush
    >
      {error && <Alert tone="error">{error.message}</Alert>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Member</th>
              <th>Kind</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Last seen</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data?.items.map((u) => (
              <Fragment key={u.id}>
                <tr>
                  <td>
                    <span className="member-cell">
                      <Avatar seed={u.avatarSeed} size={24} />
                      <a href={`/u/${u.username}`}>{u.displayName}</a>
                      <span className="muted">@{u.username}</span>
                    </span>
                  </td>
                  <td>
                    <span className={`badge${kindOf(u) === "member" ? " badge--success" : ""}`}>
                      {kindOf(u)}
                    </span>
                  </td>
                  <td>
                    <select
                      value={u.role}
                      disabled={u.isDemo || setRole.isPending}
                      onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value as Role })}
                    >
                      {(["starter", "pro", "admin"] as const).map((r) => (
                        <option key={r} value={r}>
                          {LAYOUT_NAME[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="mono small">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td className="mono small">
                    {u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : "—"}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--sm"
                      aria-expanded={open === u.id}
                      onClick={() => setOpen(open === u.id ? null : u.id)}
                    >
                      Limits
                    </button>{" "}
                    <button
                      type="button"
                      className={`btn btn--sm${u.publisherBadge === "verified" ? " is-on" : ""}`}
                      aria-pressed={u.publisherBadge === "verified"}
                      disabled={vouch.isPending}
                      title="Verified publisher: a member StrategyHive vouches for. Shown as ✓ next to their name."
                      onClick={() => vouch.mutate({ id: u.id, on: u.publisherBadge !== "verified" })}
                    >
                      {u.publisherBadge === "verified" ? "✓ Verified publisher" : "Vouch"}
                    </button>{" "}
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      disabled={u.isDemo || remove.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete @${u.username} and everything they own? This can't be undone.`,
                          )
                        )
                          remove.mutate(u.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
                {open === u.id && (
                  <tr>
                    <td colSpan={6}>
                      <MemberLimits
                        userId={u.id}
                        overrides={u.limitOverrides}
                        limits={u.limits}
                        usage={u.usage}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && needsPager(list.data.total) && (
        <Pagination
          total={list.data.total}
          limit={limit}
          offset={offset}
          onLimitChange={setLimit}
          onOffsetChange={setOffset}
        />
      )}
    </Card>
  );
}

interface AuditRow {
  id: number;
  at: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  detail: unknown;
  actor: string | null;
}

function AuditTab() {
  const list = useQuery({
    queryKey: ["admin", "audit"],
    queryFn: () => api<{ items: AuditRow[] }>("/admin/audit?limit=200"),
  });
  const page = useClientPage(list.data?.items ?? []);
  return (
    <Card title="Audit log" flush>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>What</th>
              <th>Target</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {page.pageItems.map((r) => (
              <tr key={r.id}>
                <td className="mono small">{new Date(r.at).toLocaleString()}</td>
                <td>{r.actor ? `@${r.actor}` : <span className="muted">system</span>}</td>
                <td className="mono">{r.action}</td>
                <td className="mono small">
                  {r.target_type ? `${r.target_type}:${r.target_id?.slice(0, 8)}` : "—"}
                </td>
                <td className="mono small truncate">{JSON.stringify(r.detail)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {needsPager(page.pager.total) && <Pagination {...page.pager} />}
    </Card>
  );
}

function AdminInner() {
  // The tab lives in the URL (?tab=Verification), so a reload or a link keeps it.
  const [params, setParams] = useSearchParams();
  // "Bots & jobs" was this tab's name; old links still land on it.
  const asked = params.get("tab") === "Bots & jobs" ? "Bots" : params.get("tab");
  const tab: Tab = (TABS as readonly string[]).includes(asked ?? "") ? (asked as Tab) : "Overview";
  const setTab = (t: Tab) => setParams(t === "Overview" ? {} : { tab: t }, { replace: true });
  // Requests waiting for review, shown on the tab so they are seen from any tab.
  const waiting = useQuery({
    queryKey: ["admin", "badge-requests", "pending"],
    queryFn: () => api<{ items: unknown[] }>("/hub/admin/badge-requests?status=pending"),
    refetchInterval: 60_000,
  }).data?.items.length;
  const alerts = useOpsAlerts().data?.items ?? [];
  const countFor = (t: Tab) => {
    const area =
      t === "Bots" ? "bots" : t === "Market data" ? "market-data" : t === "Overview" ? "system" : null;
    if (!area) return null;
    const mine = alerts.filter((a) => a.area === area);
    if (!mine.length) return null;
    return { n: mine.length, error: mine.some((a) => a.severity === "error") };
  };
  // On a phone the tab bar scrolls sideways: keep the selected tab in view.
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bar.current
      ?.querySelector<HTMLElement>('[aria-selected="true"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [tab, waiting, alerts.length]); // the count widens the bar, so scroll again when it arrives
  return (
    <div className="stack">
      <PageHead title="Admin console" />
      <div className="subnav subnav--scroll" role="tablist" ref={bar}>
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`subnav__link${tab === t ? " active" : ""}`}
            onClick={() => setTab(t)}
          >
            {t === "Verification" && waiting ? `${t} · ${waiting}` : t}
            {countFor(t) && (
              <span
                className={`badge ${countFor(t)!.error ? "badge--danger" : "badge--warn"}`}
                aria-label={`${countFor(t)!.n} alert${countFor(t)!.n === 1 ? "" : "s"}`}
              >
                {countFor(t)!.n}
              </span>
            )}
          </button>
        ))}
      </div>
      {tab === "Overview" && <OverviewTab />}
      {tab === "Activity" && <ActivityTab />}
      {tab === "Simulation" && <SimTab />}
      {tab === "Feedback" && <FeedbackTab />}
      {tab === "Verification" && <VerificationTab />}
      {tab === "League" && <LeagueTab />}
      {tab === "Bots" && <BotsTab />}
      {tab === "Market data" && <MarketDataTab />}
      {tab === "Members" && <MembersTab />}
      {tab === "Limits" && <LimitsTab />}
      {tab === "Audit log" && <AuditTab />}
    </div>
  );
}

export function AdminPage() {
  return (
    <Guard role="admin">
      <AdminInner />
    </Guard>
  );
}
